import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { API, authFetch, useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { 
  ArrowLeft, Download, Share2, Copy, QrCode, 
  Calendar, Weight, Fingerprint, Syringe, FileText,
  Dog, Cat, Bird, Rabbit, PawPrint, CheckCircle, Clock, AlertCircle,
  User, Mail, Phone, Shield
} from "lucide-react";

const speciesIcons = { dog: Dog, cat: Cat, bird: Bird, rabbit: Rabbit, other: PawPrint };
const speciesLabels = { dog: "Cane", cat: "Gatto", bird: "Uccello", rabbit: "Coniglio", other: "Altro" };

// Helper to capitalize breed names properly
const formatBreed = (breed) => {
  if (!breed) return null;
  return breed
    .toLowerCase()
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

export default function PetDocument() {
  const { petId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [documentData, setDocumentData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [shareLink, setShareLink] = useState(null);
  const [generatingShare, setGeneratingShare] = useState(false);
  const printRef = useRef(null);

  useEffect(() => {
    fetchDocumentData();
  }, [petId]);

  const fetchDocumentData = async () => {
    try {
      const response = await authFetch(`${API}/pets/${petId}/document`);
      if (response.ok) {
        setDocumentData(await response.json());
      } else {
        toast.error("Errore nel caricamento del documento");
        navigate('/dashboard');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error("Errore di connessione");
    } finally {
      setLoading(false);
    }
  };

  const generateShareLink = async () => {
    setGeneratingShare(true);
    try {
      const response = await authFetch(`${API}/pets/${petId}/share-data`);
      if (response.ok) {
        const data = await response.json();
        const link = `${window.location.origin}/shared/${data.share_token}`;
        setShareLink(link);
        toast.success("Link di condivisione generato!");
      } else {
        toast.error("Errore nella generazione del link");
      }
    } catch (error) {
      toast.error("Errore di connessione");
    } finally {
      setGeneratingShare(false);
    }
  };

  const copyShareLink = () => {
    if (shareLink) {
      navigator.clipboard.writeText(shareLink);
      toast.success("Link copiato!");
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const getDefaultPetImage = (species) => {
    const images = {
      dog: "https://images.unsplash.com/photo-1587300003388-59208cc962cb?w=400&q=80",
      cat: "https://images.unsplash.com/photo-1514888286974-6c03e2ca1dba?w=400&q=80",
      bird: "https://images.unsplash.com/photo-1452570053594-1b985d6ea890?w=400&q=80",
      rabbit: "https://images.unsplash.com/photo-1585110396000-c9ffd4e4b308?w=400&q=80",
      other: "https://images.unsplash.com/photo-1425082661705-1834bfd09dca?w=400&q=80"
    };
    return images[species] || images.other;
  };

  const getVaccineStatus = (expiryDate) => {
    if (!expiryDate) return 'valid';
    const today = new Date();
    const expiry = new Date(expiryDate);
    const daysUntilExpiry = Math.ceil((expiry - today) / (1000 * 60 * 60 * 24));
    if (daysUntilExpiry < 0) return 'expired';
    if (daysUntilExpiry <= 30) return 'expiring';
    return 'valid';
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAFAFA]">
        <div className="animate-pulse">
          <FileText size={48} className="text-blue-600" />
        </div>
      </div>
    );
  }

  if (!documentData) return null;

  const { pet, owner, age_text, vaccines, documents } = documentData;
  const SpeciesIcon = speciesIcons[pet.species] || PawPrint;

  return (
    <div className="min-h-screen bg-slate-100" data-testid="pet-document-page">
      {/* Header - Not printed */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-40 print:hidden">
        <div className="px-5 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate(`/pets/${petId}`)}
              className="w-10 h-10 rounded-xl bg-[#F5F5F7] flex items-center justify-center"
            >
              <ArrowLeft className="text-[#1D1D1F]" size={20} />
            </button>
            <div>
              <h1 className="text-lg font-bold text-[#1D1D1F]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Documento Digitale
              </h1>
              <p className="text-sm text-[#86868B]">{pet.name}</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={handlePrint}
              variant="outline"
              size="sm"
              className="rounded-xl"
            >
              <Download size={16} className="mr-1" />
              PDF
            </Button>
            <Button
              onClick={generateShareLink}
              disabled={generatingShare}
              size="sm"
              className="bg-blue-600 hover:bg-blue-700 rounded-xl"
            >
              <Share2 size={16} className="mr-1" />
              Condividi
            </Button>
          </div>
        </div>

        {/* Share Link */}
        {shareLink && (
          <div className="px-5 pb-4">
            <div className="bg-blue-50 rounded-xl p-3 flex items-center gap-3">
              <div className="flex-1">
                <p className="text-xs text-blue-600 font-medium mb-1">Link di condivisione (valido 24h)</p>
                <p className="text-sm text-blue-800 font-mono truncate">{shareLink}</p>
              </div>
              <Button onClick={copyShareLink} size="sm" variant="ghost" className="text-blue-600">
                <Copy size={16} />
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Document Content - Printable */}
      <div ref={printRef} className="max-w-2xl mx-auto p-6 print:p-0 print:max-w-none">
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden print:rounded-none print:shadow-none">
          
          {/* Document Header */}
          <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-6 print:p-8">
            <div className="flex items-center gap-2 mb-4">
              <Shield className="text-white/80" size={20} />
              <span className="text-sm font-medium text-white/80 uppercase tracking-wide">
                Documento Sanitario Digitale
              </span>
            </div>
            
            <div className="flex items-center gap-5">
              <div className="w-24 h-24 rounded-2xl overflow-hidden border-4 border-white/30 flex-shrink-0">
                <img 
                  src={pet.photo_url || getDefaultPetImage(pet.species)}
                  alt={pet.name}
                  className="w-full h-full object-cover"
                />
              </div>
              <div>
                <h2 className="text-3xl font-bold mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {pet.name}
                </h2>
                <div className="flex items-center gap-2 text-white/80">
                  <SpeciesIcon size={16} />
                  <span>{speciesLabels[pet.species]}</span>
                  {pet.breed && <span>• {formatBreed(pet.breed)}</span>}
                </div>
                {age_text && (
                  <p className="text-white/70 text-sm mt-1">{age_text}</p>
                )}
              </div>
            </div>
          </div>

          {/* Pet Info Grid */}
          <div className="p-6 border-b border-slate-200">
            <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-4">
              Informazioni Animale
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {pet.birth_date && (
                <div className="bg-slate-50 rounded-xl p-3">
                  <div className="flex items-center gap-2 text-slate-500 mb-1">
                    <Calendar size={14} />
                    <span className="text-xs">Data di nascita</span>
                  </div>
                  <p className="font-semibold text-slate-800">{pet.birth_date}</p>
                </div>
              )}
              {pet.weight && (
                <div className="bg-slate-50 rounded-xl p-3">
                  <div className="flex items-center gap-2 text-slate-500 mb-1">
                    <Weight size={14} />
                    <span className="text-xs">Peso</span>
                  </div>
                  <p className="font-semibold text-slate-800">{pet.weight} kg</p>
                </div>
              )}
              {pet.microchip && (
                <div className="bg-slate-50 rounded-xl p-3 col-span-2">
                  <div className="flex items-center gap-2 text-slate-500 mb-1">
                    <Fingerprint size={14} />
                    <span className="text-xs">Microchip</span>
                  </div>
                  <p className="font-mono font-semibold text-slate-800">{pet.microchip}</p>
                </div>
              )}
            </div>
          </div>

          {/* Owner Info */}
          <div className="p-6 border-b border-slate-200 bg-slate-50">
            <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-3">
              Proprietario
            </h3>
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                <User className="text-blue-600" size={20} />
              </div>
              <div>
                <p className="font-semibold text-slate-800">{owner.name}</p>
                <p className="text-sm text-slate-500">{owner.email}</p>
              </div>
            </div>
          </div>

          {/* Vaccines Section */}
          <div className="p-6 border-b border-slate-200">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wide flex items-center gap-2">
                <Syringe size={16} className="text-emerald-600" />
                Registro Vaccinale
              </h3>
              <span className="text-xs bg-slate-100 px-2 py-1 rounded-full text-slate-600">
                {vaccines.length} vaccin{vaccines.length !== 1 ? 'i' : 'o'}
              </span>
            </div>
            
            {vaccines.length === 0 ? (
              <p className="text-slate-400 text-sm italic">Nessun vaccino registrato</p>
            ) : (
              <div className="space-y-3">
                {vaccines.map((vaccine, index) => {
                  const status = getVaccineStatus(vaccine.expiry_date);
                  return (
                    <div 
                      key={vaccine.vaccine_id || index}
                      className="flex items-center justify-between p-3 bg-slate-50 rounded-xl"
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                          status === 'valid' ? 'bg-emerald-100' :
                          status === 'expiring' ? 'bg-amber-100' : 'bg-red-100'
                        }`}>
                          {status === 'valid' && <CheckCircle className="text-emerald-600" size={16} />}
                          {status === 'expiring' && <Clock className="text-amber-600" size={16} />}
                          {status === 'expired' && <AlertCircle className="text-red-600" size={16} />}
                        </div>
                        <div>
                          <p className="font-medium text-slate-800">{vaccine.name}</p>
                          <p className="text-xs text-slate-500">
                            Somministrato: {vaccine.date_administered}
                            {vaccine.vet_name && ` • ${vaccine.vet_name}`}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <span className={`text-xs font-medium px-2 py-1 rounded-full ${
                          status === 'valid' ? 'bg-emerald-100 text-emerald-700' :
                          status === 'expiring' ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'
                        }`}>
                          {status === 'valid' && 'Valido'}
                          {status === 'expiring' && 'In scadenza'}
                          {status === 'expired' && 'Scaduto'}
                        </span>
                        {vaccine.expiry_date && (
                          <p className="text-xs text-slate-400 mt-1">Scad: {vaccine.expiry_date}</p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Documents Section */}
          <div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wide flex items-center gap-2">
                <FileText size={16} className="text-blue-600" />
                Documenti
              </h3>
              <span className="text-xs bg-slate-100 px-2 py-1 rounded-full text-slate-600">
                {documents.length} document{documents.length !== 1 ? 'i' : 'o'}
              </span>
            </div>
            
            {documents.length === 0 ? (
              <p className="text-slate-400 text-sm italic">Nessun documento registrato</p>
            ) : (
              <div className="space-y-2">
                {documents.map((doc, index) => (
                  <div 
                    key={doc.document_id || index}
                    className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl"
                  >
                    <FileText className="text-blue-500" size={18} />
                    <div>
                      <p className="font-medium text-slate-800">{doc.title}</p>
                      <p className="text-xs text-slate-500">{doc.doc_type}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="p-4 bg-slate-100 text-center">
            <p className="text-xs text-slate-500">
              Documento generato da <strong>Zampa</strong> • {new Date().toLocaleDateString('it-IT')}
            </p>
            <p className="text-xs text-slate-400 mt-1">
              Questo documento è a scopo informativo. Per certificazioni ufficiali, consultare il veterinario.
            </p>
          </div>
        </div>
      </div>

      {/* Print Styles */}
      <style>{`
        @media print {
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          .print\\:hidden { display: none !important; }
          .print\\:p-0 { padding: 0 !important; }
          .print\\:max-w-none { max-width: none !important; }
          .print\\:rounded-none { border-radius: 0 !important; }
          .print\\:shadow-none { box-shadow: none !important; }
          .print\\:p-8 { padding: 2rem !important; }
        }
      `}</style>
    </div>
  );
}
